import { useEffect, useRef } from 'react'
import type { OverridedMixpanel } from 'mixpanel-browser'
import { useExperiments } from 'business-logic/experiments'
import { RunningExperiments } from 'RunningExperiments'
import { UniqueUserId } from '@xylabs/pixel'
import { useLocation } from 'react-router-dom'
import { TestData } from './UserEvents/TestData'
import randomBytes from 'randombytes'

const MIXPANEL_KEY = process.env.REACT_APP_MIXPANEL_API_KEY || ''
let mixpanel: { default: OverridedMixpanel }

async function initializeMixpanel () {
  if (!MIXPANEL_KEY) return

  mixpanel = await import('mixpanel-browser')
  mixpanel.default.init(MIXPANEL_KEY, {
    debug: true,
    track_pageview: true,
  })

  const sessionId = randomBytes(16).toString('hex')
  mixpanel.default.register({ testData: new TestData().data, sessionId }) 
  mixpanel.default.identify(new UniqueUserId().id)
}

function mixpanelTrackPageview (pathname: string) {
  if (mixpanel) {
    mixpanel.default.track_pageview({ pathname, testData: new TestData().data })
  }
}

export function mixpanelTrackEvent (event: string, properties: Record<string, unknown>) {
  if (mixpanel) {
    mixpanel.default.track(event, properties)
  }
}

export function useMixpanelPageview () {
  const location = useLocation()
  const mixpanelEnabledRef = useRef(false)
  const mixpanelExperiment = useExperiments<() => Promise<void>>(
    RunningExperiments.MIXPANEL_ENABLED,
    [
      { name: 'a/enabled', weight: 100 },
      { name: 'b/disabled', weight: 0 },
    ]
  )

  const mixpanelConditionalInit = mixpanelExperiment.selectVariant({
    'a/enabled': async () => {
      if (mixpanelEnabledRef.current) return

      mixpanelEnabledRef.current = true
      return initializeMixpanel()
    },
    'b/disabled': async () => {},
  }, async () => {})

  useEffect(() => {
    mixpanelTrackPageview(location.pathname)
  }, [location.pathname])

  useEffect(() => {
    mixpanelConditionalInit()
  }, [mixpanelConditionalInit])
}